import { useGetEnterpriseQuery, useWebStudioChainDispensariesQuery } from 'types/graphql';

import useStores from 'shared/hooks/use-stores';

type UseDefaultEnterpriseCNameResult = {
  /** First dispensary cName for the enterprise's chain, or undefined if none resolved yet. */
  cName?: string;
  /** True while either the enterprise or its dispensaries are still being fetched. */
  loading: boolean;
};

/**
 * Resolves a default store cName for an enterprise so the CMS editor always has a
 * dispensary to preview against. Maps `enterpriseId` -> `uniqueName` (chain id) ->
 * the chain's first dispensary cName, mirroring the consumer StoreSelector default.
 */
export function useDefaultEnterpriseCName(enterpriseId: string): UseDefaultEnterpriseCNameResult {
  const { apolloClient } = useStores();

  const enterpriseResult = useGetEnterpriseQuery({
    client: apolloClient,
    skip: !enterpriseId,
    variables: { enterpriseId },
  });

  const chainId = enterpriseResult.data?.getEnterprise?.uniqueName;

  const dispensariesResult = useWebStudioChainDispensariesQuery({
    client: apolloClient,
    skip: !chainId,
    variables: { chainId: chainId ?? '' },
  });

  // Mirrors the marketplace StoreSelector normalizer (apps/marketplace/src/cms/components/store-selector.tsx):
  // take the chain's first dispensary and fall back to its id when cName is unset.
  const firstDispensary = dispensariesResult.data?.chainDispensaryNamesAndIds?.find((dispensary) => dispensary?.id);
  const cName = firstDispensary?.cName ?? firstDispensary?.id ?? undefined;

  const loading = enterpriseResult.loading || (!!chainId && dispensariesResult.loading);

  return { cName, loading };
}
